// app/frontend/pages/Onboarding/Onboarding.tsx
import { useState } from "react";
import { useForm } from "@mantine/form";
import { Box, Container } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconAlertCircle } from "@tabler/icons-react";
import { router } from "@inertiajs/react";
import { L2KStepper } from "../../components/ui/Stepper";
import { SEO } from "../../components/common/SEO";
import { stepConfigs } from "./stepConfigs";
import type { OnboardingFormValues, OnboardingPageProps } from "./types";

export default function Onboarding({ user }: OnboardingPageProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeStep, setActiveStep] = useState<number | undefined>(undefined);

  const form = useForm<OnboardingFormValues>({
    initialValues: {
      skill_level: user.skill_level || "",
      city: user.city || "",
      country: user.country || "",
      dance_goals: user.dance_goals || "",
    },
    validate: {
      skill_level: (value) =>
        value ? null : "Please select your skill level",
    },
  });

  // Validate specific steps before proceeding
  const validateCurrentStep = (stepId: string): boolean => {
    switch (stepId) {
      case "skill-level":
        return !form.validateField("skill_level").hasError;
      default:
        return true;
    }
  };

  const handleSubmit = async (values: OnboardingFormValues) => {
    setIsSubmitting(true);

    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;

    try {
      const response = await fetch("/onboarding", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
        body: JSON.stringify({ user: values }),
      });

      if (response.ok) {
        setIsSubmitted(true);

        notifications.show({
          color: "green",
          title: "Profile Complete!",
          message: "Your learning experience has been personalized",
          icon: <IconCheck />,
          autoClose: 3000,
        });

        // Redirect to dashboard after a brief delay
        setTimeout(() => {
          router.visit("/dashboard");
        }, 2000);
      } else {
        const data = await response.json().catch(() => ({}));

        notifications.show({
          color: "red",
          title: "Something went wrong",
          message: data.error || "Please try again",
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });
      }
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "An error occurred. Please try again.",
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse at 20% 50%, rgba(255, 152, 0, 0.15) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 20%, rgba(255, 193, 7, 0.12) 0%, transparent 50%),
          radial-gradient(ellipse at 40% 80%, rgba(255, 87, 34, 0.1) 0%, transparent 50%),
          linear-gradient(135deg, #171717 0%, #1a1a1a 25%, #171717 50%, #1f1f1f 75%, #171717 100%)
        `,
        paddingTop: "40px",
        paddingBottom: "40px",
      }}
    >
      <SEO title="Complete Your Profile - Learntokiz" />

      <Container size="md">
        <L2KStepper<OnboardingFormValues>
          steps={stepConfigs}
          form={form}
          onSubmit={handleSubmit}
          validateCurrentStep={validateCurrentStep}
          withProgressBar
          isSubmitting={isSubmitting}
          externalActive={activeStep}
          onActiveChange={setActiveStep}
          stepProps={{ isSubmitted }}
          submitText="Complete"
          nextText="Continue"
          backText="Back"
        />
      </Container>
    </Box>
  );
}
